export const PLAN_REGIONS = [
  { id: 'all', bounds: [0, -5, 610, 773] },
  { id: 'second', bounds: [0, -5, 610, 172] },
  { id: 'first', bounds: [0, 165, 610, 172] },
  { id: 'ground', bounds: [0, 334, 610, 172] },
  { id: 'basement', bounds: [0, 503, 610, 265] },
  { id: 'other', bounds: [0, 768, 610, 95] }
];

export function isInRegion(scope, region) {
  return scope === 'all' ? ['second', 'first', 'ground', 'basement'].includes(region) : scope === region;
}

export function regionAt(point) {
  const region = PLAN_REGIONS.slice(1).find(({ bounds: [x, y, width, height] }) =>
    point.x >= x && point.x <= x + width && point.y >= y && point.y <= y + height
  );
  return region ? region.id : 'unmapped';
}

function transformedBox(node, svg) {
  const box = node.getBBox();
  const matrix = svg.getScreenCTM().inverse().multiply(node.getScreenCTM());
  const points = [[box.x, box.y], [box.x + box.width, box.y],
    [box.x, box.y + box.height], [box.x + box.width, box.y + box.height]]
    .map(([x, y]) => new DOMPoint(x, y).matrixTransform(matrix));
  const x = Math.min(...points.map(point => point.x));
  const y = Math.min(...points.map(point => point.y));
  const width = Math.max(...points.map(point => point.x)) - x;
  const height = Math.max(...points.map(point => point.y)) - y;
  return { x, y, width, height, center: { x: x + width / 2, y: y + height / 2 }, matrix };
}

// The source drawing remains editable in Inkscape; geometry is measured from its SVG transforms.
export function readPlanGeometry(text) {
  const documentSvg = new DOMParser().parseFromString(text, 'image/svg+xml');
  if (documentSvg.querySelector('parsererror')) throw new Error('Invalid plan SVG');
  const svg = document.importNode(documentSvg.documentElement, true);
  if (svg.localName !== 'svg') throw new Error('Missing plan SVG');
  const container = document.createElement('div');
  container.style.cssText = 'position:absolute;left:-10000px;top:0;visibility:hidden;pointer-events:none';
  container.appendChild(svg);
  document.body.appendChild(container);
  try {
    svg.querySelectorAll('#datos,#leyenda_admin,#loading,#nbnf,script,foreignObject,metadata').forEach(node => node.remove());
    svg.querySelectorAll('#path1901,#path1903').forEach(node => node.remove());
    svg.querySelectorAll('[aria-label]').forEach(node => {
      if (/^P(?:-?\d+|S)$/i.test(node.getAttribute('aria-label').trim())) node.remove();
    });
    const geometries = Array.from(svg.querySelectorAll('[id^="mesa_"]')).map(node => {
      const id = node.id.slice(5);
      const box = transformedBox(node, svg);
      const { a, b, c, d, e, f } = box.matrix;
      const attributes = {};
      Array.from(node.attributes).filter(attribute =>
        ['x', 'y', 'width', 'height', 'd', 'points', 'rx', 'ry', 'cx', 'cy', 'r'].includes(attribute.name)
      ).forEach(attribute => { attributes[attribute.name] = attribute.value; });
      const marker = (prefix, fraction) => {
        const original = svg.querySelector(`[id="${prefix}_${id}"]`);
        return original ? transformedBox(original, svg).center : { x: box.x + box.width * fraction, y: box.y + box.height - 4 };
      };
      return {
        id, tag: node.localName, attributes, transform: `matrix(${a},${b},${c},${d},${e},${f})`,
        x: box.x, y: box.y, width: box.width, height: box.height, center: box.center,
        region: regionAt(box.center), photoMarker: marker('nf', 0.3), bioMarker: marker('nb', 0.7)
      };
    });
    if (new Set(geometries.map(geometry => geometry.id)).size !== geometries.length) throw new Error('Duplicate desk IDs');
    svg.querySelectorAll('[id^="mesa_"],[id^="nf_"],[id^="nb_"]').forEach(node => node.remove());
    const background = Array.from(svg.children).filter(node => ['defs', 'g', 'path', 'rect', 'text'].includes(node.localName))
      .map(node => new XMLSerializer().serializeToString(node)).join('');
    return { background, geometries };
  } finally {
    container.remove();
  }
}
